import React from 'react';
import { ArcStack, ArcToolbar } from '@src/core/ui';
import { useDispatch, useSelector } from 'react-redux';
import { selectDrone, selectTerrainMode, selectWaypoints, setSidebarType, useCreateMissionMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { MissionWaypoint } from '@src/features/order/pages/upsert-mission-page/redux/types';
import { IRouteForm, SidebarType } from '@src/features/order/pages/upsert-mission-page/entities';
import { useForm } from 'react-hook-form';
import { EMPTY_VALUE } from '@src/core/config';
import { SettingUnitType, useCheckAndPlanMutation, useGetSettingByUnitType } from '@src/core/features';
import { EnterRouteForm } from '@src/features/order/pages/upsert-mission-page/components';
import { OrderIdParams } from '@src/core/entities';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { routesConfig } from '@src/core/routes';
import { useCancelOrderMutation } from '@src/features/order/redux';
import { CreateRouteSidebarProps } from './types';
import { arcConvertBetweenUnitTypes, arcToFixed } from '@src/core/extensions';
import { getUnitType } from '@src/core/features/settings/units';
import { MeasurementType } from '@src/core/features/settings/entities';
import { UnitType, UnitSystemType } from '@src/core/enums';
import { trackError, toTrackedError } from '@src/core/sentry';
import { addOptimisticMissionSerial } from '@src/features/drone/redux/actions';
import {
  buildConfirmOrderPath,
  ConfirmFlowOrigin,
  resolveMissionRouteBackNavigation,
} from '@src/features/order/navigation';

// Settings values arrive in SI (m, m/s); the route form inputs are labeled in the
// user's display unit. Convert SI -> display before pre-filling (same as update-route).
const siToDisplay = (value: number | string | undefined | null, measurement: MeasurementType): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY_VALUE;
  const converted = arcConvertBetweenUnitTypes(num, measurement, UnitType.SI, getUnitType());
  return arcToFixed(converted, 1);
};

const CreateRouteSidebar: React.FC<CreateRouteSidebarProps> = (props) => {
  const { orderId, onSubmit: onSubmitProps, onCancel: onCancelProps } = props;
  const params = useParams<OrderIdParams>();
  const [queryParams] = useSearchParams();
  const effectiveOrderId = orderId || params.orderId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const droneSerial = queryParams.get('droneSerial');
  const isStepByStep = Boolean(queryParams.get('step-by-step'));
    
  const wps = useSelector(selectWaypoints);
  const selectedDrone = useSelector(selectDrone);
  const terrainMode = useSelector(selectTerrainMode);
  const speed = useGetSettingByUnitType(SettingUnitType.DELIVERY_SPEED);
  const deliveyMode = useGetSettingByUnitType(SettingUnitType.DELIVERY_MODE);
  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const deliveryAltitude = useGetSettingByUnitType(SettingUnitType.DELIVERY_ALTITUDE);

  const [cancelOrder, { isLoading: isCanceling }] = useCancelOrderMutation();
  const [createMission, { isLoading: isSubmitting }] = useCreateMissionMutation();
  const [, { isLoading: isCheckingRoute }] = useCheckAndPlanMutation();


  const goBack = () => {
    // If user came from position-overlay (has droneSerial), navigate back to previous page
    if (droneSerial) {
      navigate(-1);
      return;
    }
    const { path, replace } = resolveMissionRouteBackNavigation(
      'create-mission',
      effectiveOrderId,
      isStepByStep,
    );
    if (isStepByStep && effectiveOrderId != null) {
      navigate(path, { replace });
      return;
    }
    dispatch(setSidebarType(SidebarType.SELECT_DRONE));
  };

  const onCancel = () => {
    if (onCancelProps) {
      onCancelProps();
      return;
    }
    cancelOrder(effectiveOrderId).unwrap()
      .then(() => {
        toast.success('Order has been canceled');
        navigate(routesConfig.orders, { replace: true });
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'cancelOrderCreateRoute' } });
      });
  };

  const onSubmit = (form: IRouteForm, waypoints: MissionWaypoint[]) => {
    if (onSubmitProps) {
      onSubmitProps(form, waypoints);
      return;
    }

    if (!selectedDrone) return;

    // Waypoints are already converted to SI in toMissionWaypoints; the wire is always
    // SI, so unitSystem is a constant guard (UNITS Standard R2/R6).
    createMission({
      orderId: effectiveOrderId,
      droneId: selectedDrone.id,
      deliveryMode: form.deliveryMode,
      altitudeMode: terrainMode,
      unitSystem: UnitSystemType.METRIC,
      waypoints,
    }).unwrap().then((response) => {
      toast.success('Mission has been created');
      const serial = response.drone.serialNumber;
      if (serial) dispatch(addOptimisticMissionSerial(serial));
      navigate(buildConfirmOrderPath(response.missionId, {
        origin: ConfirmFlowOrigin.WIZARD,
        droneSerial: serial,
        orderId: effectiveOrderId,
      }), { replace: true });
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'createMission' } });
    });
  };

  const form = useForm<IRouteForm>({ 
    defaultValues: {
      waypoints: [],
      speed: EMPTY_VALUE,
      deliveryMode: EMPTY_VALUE,
      takeOffAltitude: EMPTY_VALUE,
      deliveryAltitude: EMPTY_VALUE,
      winchAltitude: EMPTY_VALUE,
      destinationAltitude: EMPTY_VALUE,
    } 
  });

  React.useEffect(() => {
    form.setValue('speed', siToDisplay(speed.value, MeasurementType.SPEED));
    form.setValue('deliveryMode', deliveyMode.value);
    form.setValue('takeOffAltitude', siToDisplay(flightAltitude.value, MeasurementType.DISTANCE));
    form.setValue('deliveryAltitude', siToDisplay(deliveryAltitude.value, MeasurementType.DISTANCE));
    form.setValue('winchAltitude', siToDisplay(deliveryAltitude.value, MeasurementType.DISTANCE));
    form.setValue('destinationAltitude', siToDisplay(flightAltitude.value, MeasurementType.DISTANCE));
  }, [speed, deliveyMode, deliveryAltitude, flightAltitude]);

  return (
    <ArcStack spacing={2} height='100%'>
      <ArcToolbar 
        title='3. Enter route parameters' 
        goBack={goBack} />
      <EnterRouteForm 
        form={form} 
        goBack={onCancel} 
        onSubmit={onSubmit}
        canSubmit={wps.length > 0 && !isCheckingRoute}
        isCanceling={isCanceling}
        isSubmitting={isSubmitting} />
    </ArcStack>
  );
};

export default CreateRouteSidebar;